import React, { useState, useEffect } from "react";
import { getApiBaseUrl } from "../../../utils/api";

const API_BASE_URL = getApiBaseUrl();

const GroupForm = ({ group, users, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: group?.name || "",
    description: group?.description || "",
    background_image: group?.background_image || "",
    domain: group?.domain || "",
    user_ids: group?.user_ids || [],
    is_public: group?.is_public || false,
  });
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState(new Set(group?.user_ids || []));
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [domainSearch, setDomainSearch] = useState("");
  const [filteredUsers, setFilteredUsers] = useState(users);

  // Update form data when group prop changes
  useEffect(() => {
    if (group) {
      setFormData({
        name: group.name || "",
        description: group.description || "",
        background_image: group.background_image || "",
        domain: group.domain || "",
        user_ids: group.user_ids || [],
        is_public: group.is_public || false,
      });
      setSelectedUsers(new Set(group.user_ids || []));
    }
  }, [group]);

  // Filter users based on search term and domain
  useEffect(() => {
    let filtered = users;

    if (userSearchTerm) {
      const searchTerm = userSearchTerm.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.name?.toLowerCase().includes(searchTerm) ||
          user.email?.toLowerCase().includes(searchTerm) ||
          user.company?.toLowerCase().includes(searchTerm)
      );
    }
    if (domainSearch) {
      const domain = domainSearch.toLowerCase().replace("@", "");
      filtered = filtered.filter((user) => user.email?.toLowerCase().includes(domain));
    }

    setFilteredUsers(filtered);
  }, [users, userSearchTerm, domainSearch]);

  // Search users by domain
  const searchUsersByDomain = async () => {
    if (!domainSearch) return;

    setUsersLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/admin/users?user_id=${user.id}&domain=${encodeURIComponent(domainSearch)}`
      );
      const data = await response.json();
      setFilteredUsers(data.users || []);
    } catch (error) {
      console.error("Error searching users by domain:", error);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await onSubmit({
        ...formData,
        user_ids: Array.from(selectedUsers),
      });
    } catch (error) {
      console.error("Error submitting group:", error);
      alert(error.message || "Erro ao salvar grupo");
    } finally {
      setLoading(false);
    }
  };

  const handleUserToggle = (userId) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            {group ? "Editar Grupo" : "Criar Grupo"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Group Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Nome do Grupo *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Descrição</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 h-24"
              placeholder="Descrição do grupo..."
            />
          </div>

          {/* Domain Auto-Assignment */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Domínio de Email para Auto-Vinculação
            </label>
            <div className="space-y-2">
              <input
                type="text"
                value={formData.domain}
                onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Ex: empresa.com (usuários com @empresa.com serão automaticamente vinculados)"
              />
              <p className="text-xs text-gray-500">
                Usuários que se cadastrarem com emails deste domínio serão automaticamente
                adicionados a este grupo. Deixe vazio para desabilitar a auto-vinculação.
              </p>
            </div>
          </div>

          {/* Public Group Option */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="is_public"
                checked={formData.is_public}
                onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="is_public" className="text-sm font-medium text-gray-700">
                Grupo Público
              </label>
            </div>
            <p className="text-xs text-gray-600 mt-2">
              Se marcado, todos os usuários da plataforma terão acesso a este grupo automaticamente.
              Esta opção substitui a seleção manual de usuários e domínio.
            </p>
          </div>

          {/* Background Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Imagem de Fundo (URL)
            </label>
            <input
              type="url"
              value={formData.background_image}
              onChange={(e) => setFormData({ ...formData, background_image: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="https://exemplo.com/imagem.jpg"
            />
            {formData.background_image && (
              <div className="mt-2">
                <img
                  src={formData.background_image}
                  alt="Preview"
                  className="w-full h-32 object-cover rounded-lg"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              </div>
            )}
          </div>

          {/* User Search and Domain Filter */}
          <div className={formData.is_public ? "opacity-50 pointer-events-none" : ""}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Usuários ({selectedUsers.size} selecionados)
              {filteredUsers.length !== users.length && (
                <span className="text-sm text-blue-600 ml-2">
                  ({filteredUsers.length} de {users.length} usuários)
                </span>
              )}
              {formData.is_public && (
                <span className="text-sm text-blue-600 ml-2">
                  (Desabilitado para grupos públicos)
                </span>
              )}
            </label>

            {/* Search Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label
                  htmlFor="user-search-form"
                  className="block text-xs font-medium text-gray-600 mb-1"
                >
                  Buscar usuários
                </label>
                <input
                  type="text"
                  id="user-search-form"
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  placeholder="Nome, email ou empresa..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label
                  htmlFor="domain-search"
                  className="block text-xs font-medium text-gray-600 mb-1"
                >
                  Buscar por domínio
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    id="domain-search"
                    value={domainSearch}
                    onChange={(e) => setDomainSearch(e.target.value)}
                    placeholder="@empresa.com"
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={searchUsersByDomain}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                  >
                    Buscar
                  </button>
                </div>
              </div>
            </div>

            {/* Filter Actions */}
            {(userSearchTerm || domainSearch) && (
              <div className="mb-4 flex space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setUserSearchTerm("");
                    setDomainSearch("");
                    setFilteredUsers(users);
                  }}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded text-sm font-medium transition-colors duration-200"
                >
                  Limpar Filtros
                </button>
                <button
                  type="button"
                  onClick={() => setFilteredUsers(users)}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm font-medium transition-colors duration-200"
                >
                  Mostrar Todos
                </button>
              </div>
            )}

            <div className="border border-gray-300 rounded-lg p-4 max-h-60 overflow-y-auto">
              {usersLoading ? (
                <div className="text-center py-4">
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <p className="mt-2 text-gray-600">Carregando usuários...</p>
                </div>
              ) : filteredUsers.length === 0 ? (
                <p className="text-gray-500 text-center py-4">
                  {userSearchTerm || domainSearch
                    ? "Nenhum usuário encontrado para os filtros"
                    : "Nenhum usuário disponível"}
                </p>
              ) : (
                <div className="space-y-2">
                  {filteredUsers.map((user) => (
                    <label key={user.id} className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedUsers.has(user.id)}
                        onChange={() => handleUserToggle(user.id)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{user.name}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                        <div className="text-xs text-gray-400">{user.company}</div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-xl font-medium transition-colors duration-200"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || !formData.name}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-6 py-3 rounded-xl font-medium transition-colors duration-200 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Salvando...</span>
                </div>
              ) : group ? (
                "Atualizar Grupo"
              ) : (
                "Criar Grupo"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GroupForm;
